import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { 
  FaHome, 
  FaGraduationCap, 
  FaQuestionCircle, 
  FaChartLine, 
  FaCheckCircle, 
  FaLock, 
  FaComments,
  FaFlask,
  FaUsers,
  FaBolt,
  FaBookOpen
} from 'react-icons/fa';
import AuthService from '../services/AuthService';
import CourseService from '../services/CourseService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createSparkle, createPulse, createGlow, createShimmer, createScreenShake } from '../utils/vfx';
import { formatCourseThumbnail, getCourseTopicIcon } from '../utils/courseDisplay';
import BottomNav from './BottomNav';
import ProfileMenu from './ProfileMenu';
import './Learning.css';

const Learning = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  // Learning path: 'interactive' (interactive workshops) or 'traditional' (reading).
  // Remembered between visits so students land on the style they prefer.
  const [track, setTrack] = useState(
    () => localStorage.getItem('investnyou-track') || 'traditional'
  );

  const changeTrack = (next) => {
    soundEffects.playClick();
    setTrack(next);
    localStorage.setItem('investnyou-track', next);
  };


  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        
        // Check if user is authenticated
        if (!AuthService.isAuthenticated()) {
          navigate('/login', { replace: true });
          return;
        }

        // Check if user exists in context
        if (!state.user) {
          navigate('/login', { replace: true });
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user]);

  // Fetch courses from backend
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadCourses = async () => {
      try {
        setCoursesLoading(true);
        const coursesData = await CourseService.getCourses();
        setCourses(coursesData);
      } catch (error) {
        toast.error('Failed to load courses');
        
        // Fallback to empty array
        setCourses([]);
      } finally {
        setCoursesLoading(false);
      }
    };

    loadCourses();
  }, [isAuthenticated]);

  // Courses for the selected path. Anything without a track (legacy rows,
  // AP courses) stays on the traditional side so nothing disappears.
  const visibleCourses = courses.filter((course) =>
    track === 'interactive'
      ? course.track === 'interactive'
      : course.track !== 'interactive'
  );

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="learning-loading">
        <div className="learning-loading-content">
          <div className="learning-spinner"></div>
          <p className="learning-loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="learning-container learning-content">
      {/* Top Bar */}
      <div className="learning-top-bar">
        <div className="learning-top-content">
          <div className="learning-top-left">
            <button 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e, '#3b82f6');
                navigate('/dashboard');
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="learning-back-button"
            >
              ←
            </button>
            <h1 className="learning-title">Learning</h1>
          </div>
          <ProfileMenu />
        </div>
      </div>

      <div className="learning-main">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="learning-header"
        >
          <h2 className="learning-header-title">
            Choose Your Learning Path
          </h2>
          <p className="learning-header-subtitle">
            Master financial literacy at your own pace!
          </p>
        </motion.div>

        {/* Track switcher — same topics, two ways to learn them */}
        <div className="track-switcher" role="tablist" aria-label="Learning path">
          <button
            role="tab"
            aria-selected={track === 'interactive'}
            className={`track-tab ${track === 'interactive' ? 'active' : ''}`}
            onClick={(e) => {
              createRipple(e, '#ff4d6d');
              changeTrack('interactive');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaBolt />
            <span className="track-tab-label">Interactive Lessons</span>
            <span className="track-tab-hint">Step-by-step workshops</span>
          </button>
          <button
            role="tab"
            aria-selected={track === 'traditional'}
            className={`track-tab ${track === 'traditional' ? 'active' : ''}`}
            onClick={(e) => {
              createRipple(e, '#ff4d6d');
              changeTrack('traditional');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaBookOpen />
            <span className="track-tab-label">Traditional Lessons</span>
            <span className="track-tab-hint">Read at your own pace</span>
          </button>
        </div>

        {/* Course Grid */}
        {coursesLoading ? (
          <div className="learning-courses-loading">
            <div className="learning-courses-spinner"></div>
            <p className="learning-courses-loading-text">Loading courses...</p>
          </div>
        ) : visibleCourses.length === 0 ? (
          <div className="track-empty">
            <FaBolt className="track-empty-icon" />
            <h3>No {track === 'interactive' ? 'interactive' : 'traditional'} courses yet</h3>
            <p>
              {track === 'interactive'
                ? 'Interactive workshops are on their way. Switch to Traditional Lessons to keep learning.'
                : 'Switch to Interactive Lessons to see the available workshops.'}
            </p>
          </div>
        ) : (
          <div className="learning-courses-grid">
            {visibleCourses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`learning-course-card ${course.isLocked ? 'locked' : ''}`}
                onClick={(e) => {
                  if (!course.isLocked) {
                    soundEffects.playClick();
                    createRipple(e, '#3b82f6');
                    createPulse(e.currentTarget, 1.05, 200);
                    setTimeout(() => navigate(`/course/${course.id}`), 200);
                  } else {
                    soundEffects.playError();
                    createScreenShake(e.currentTarget, 3, 300);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!course.isLocked) {
                    soundEffects.playHover();
                    createGlow(e.currentTarget, '#3b82f6', 300);
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div className={`learning-course-content ${course.completedLessons === course.lessonsCount && course.lessonsCount > 0 ? 'learning-course-completed' : ''}`}>
                  <div className="learning-course-thumbnail">
                    {getCourseTopicIcon(course) ? (
                      <img
                        src={getCourseTopicIcon(course)}
                        alt=""
                        aria-hidden="true"
                        className="learning-course-topic-icon"
                      />
                    ) : (
                      formatCourseThumbnail(course.thumbnail)
                    )}
                  </div>
                  
                  <div className="learning-course-info">
                    <div className="learning-course-header">
                      <h3 className="learning-course-title">
                        {course.title}
                      </h3>
                      {course.isLocked && (
                        <FaLock className="learning-course-icon" />
                      )}
                      {course.completedLessons === course.lessonsCount && course.lessonsCount > 0 && (
                        <FaCheckCircle 
                          className="learning-course-icon-completed"
                          onMouseEnter={(e) => {
                            createSparkle(e.currentTarget, 5);
                            soundEffects.playSparkle();
                          }}
                        />
                      )}
                    </div>
                    
                    <p className="learning-course-description">
                      {course.description}
                    </p>
                    
                    <div className="learning-course-footer">
                      <div className="learning-course-stats">
                        <div className="learning-course-lessons">
                          {course.completedLessons || 0}/{course.lessonsCount || 0} lessons
                        </div>
                        
                        <div className="learning-course-progress-container">
                          <div
                            className={`learning-course-progress-bar ${course.color}`}
                            style={{ width: `${course.progress || 0}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className={`learning-course-status ${course.color}`}>
                        {course.isLocked ? 'Locked' : 
                         course.completedLessons === course.lessonsCount && course.lessonsCount > 0 ? 'Complete' : 'Start'}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Learning Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="learning-tips"
        >
          <h3 className="learning-tips-title">💡 Learning Tips</h3>
          <ul className="learning-tips-list">
            <li>• Complete lessons in order for the best learning experience</li>
            <li>• Take notes and review key concepts regularly</li>
            <li>• Practice with the portfolio simulator after completing 2 courses</li>
            <li>• Ask questions anytime using the Ask feature</li>
          </ul>
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Learning;
